import { Head } from "@inertiajs/react"
import { useCallback, useRef, useState } from "react"

import type {
  ActionItem,
  AgentStatusLoaderState,
} from "@/components/agents/agent-status-loader"
import { ChatInput } from "@/components/ask/chat-input"
import { MessageList } from "@/components/ask/message-list"
import type { TurnMessage } from "@/components/ask/message-list"
import type { StatusEvent } from "@/hooks/use-chat-channel"
import { useChatChannel } from "@/hooks/use-chat-channel"
import AppLayout from "@/layouts/app-layout"
import {
  askPath,
  completeConversationPath,
  messagesConversationPath,
} from "@/routes"
import type { BreadcrumbItem, Chat, ChatMessage } from "@/types"

interface ShowProps {
  chat: Chat
  messages: ChatMessage[]
  user_first_name?: string
}

function getCsrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!
    .content
}

// Pick the result-bubble text for a completed tool-running turn. Prefer the
// server's persisted message content (the complete final answer) over the
// streamed buffer, which can be truncated. When the entire answer streamed
// before the first tool, the server content and the `intro` are the same
// message — strip the duplicated intro prefix so it isn't shown twice (and
// yields an empty bubble that doesn't render).
function resultContent(
  serverContent: string | null | undefined,
  streamed: string | null,
  intro: string | null,
): string | null {
  let text =
    serverContent && serverContent.length > 0 ? serverContent : streamed
  if (!text) return null
  if (intro && text.startsWith(intro)) {
    text = text.slice(intro.length).replace(/^\s+/, "")
  }
  return text.length > 0 ? text : null
}

export default function Show({ chat, messages: initialMessages }: ShowProps) {
  const [messages, setMessages] = useState<TurnMessage[]>(initialMessages)
  const [streamingContent, setStreamingContent] = useState<string | null>(null)
  // IMP-190: narration streamed BEFORE the first tool call, frozen so the
  // progress loader renders between it and the results (not below them).
  const [introContent, setIntroContent] = useState<string | null>(null)
  const lastMessage = initialMessages[initialMessages.length - 1]
  const needsCompletion = lastMessage?.role === "user"
  const [isWaiting, setIsWaiting] = useState(needsCompletion)
  const [chatTitle, setChatTitle] = useState(chat.title)
  const [agentStatus, setAgentStatus] = useState<AgentStatusLoaderState>(
    needsCompletion ? "thinking" : "thinking",
  )
  const [agentActions, setAgentActions] = useState<ActionItem[]>([])
  const completionTriggered = useRef(false)
  // Live mirror of the post-tool stream + a once-per-turn latch for freezing the
  // intro, so the tool_call callback can read the current buffer without stale
  // closure state.
  const streamingContentRef = useRef<string | null>(null)
  const toolsStartedRef = useRef(false)
  // Live mirror of agentActions so onMessageComplete reads the final set without
  // a stale closure (and without nesting setState calls).
  const agentActionsRef = useRef<ActionItem[]>([])

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "New session", href: askPath() },
    {
      title: chatTitle ?? "New conversation",
      href: askPath() + "/" + chat.uid,
    },
  ]

  const onConnected = useCallback(() => {
    if (!needsCompletion || completionTriggered.current) return
    completionTriggered.current = true

    void fetch(completeConversationPath(chat.uid), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
    })
  }, [needsCompletion, chat.uid])

  // Mirror agentActions into state + ref together so callbacks read the latest.
  const updateActions = useCallback(
    (updater: (prev: ActionItem[]) => ActionItem[]) => {
      setAgentActions((prev) => {
        const next = updater(prev)
        agentActionsRef.current = next
        return next
      })
    },
    [],
  )

  const onChunk = useCallback(
    (content: string) => {
      setIsWaiting(false)
      updateActions((prev) =>
        prev.map((a) =>
          a.status === "active" ? { ...a, status: "completed" as const } : a,
        ),
      )
      setAgentStatus("done")
      setStreamingContent((prev) => {
        const next = (prev ?? "") + content
        streamingContentRef.current = next
        return next
      })
    },
    [updateActions],
  )

  const onMessageComplete = useCallback(
    (message: ChatMessage) => {
      setIsWaiting(false)
      const intro = introContent
      const results = streamingContentRef.current
      const completed = agentActionsRef.current.map((a) =>
        a.status === "active" ? { ...a, status: "completed" as const } : a,
      )
      // IMP-190: persist the turn so it keeps the intro → progress/Done → results
      // ordering. A flow that ran tools carries the pre-tool narration (intro) +
      // completed actions, with the final answer as the bubble content; a plain
      // (no-tool) turn appends as-is.
      setMessages((prev) => {
        if (prev.some((m) => m.uid === message.uid)) return prev
        const turn: TurnMessage =
          completed.length > 0
            ? {
                ...message,
                // Use the authoritative persisted message content for the result
                // bubble — NOT the streamed buffer. The buffer is lossy: the
                // server's artifact/backtick holdback and our per-tool clears can
                // truncate it mid-message (the "cut off" report). The persisted
                // assistant message is the complete final answer and, since each
                // tool round is its own message, carries no between-tool
                // narration. Fall back to the stream only if the server somehow
                // sent nothing.
                content: resultContent(message.content, results, intro),
                intro,
                actions: completed,
              }
            : message
        return [...prev, turn]
      })
      // Reset the live stream — the completed turn now lives in `messages`.
      setStreamingContent(null)
      streamingContentRef.current = null
      setIntroContent(null)
      toolsStartedRef.current = false
      setAgentActions([])
      agentActionsRef.current = []
      setAgentStatus("thinking")
    },
    [introContent],
  )

  const onStatus = useCallback(
    (event: StatusEvent) => {
      if (event.status === "tool_call") {
        const label = event.label ?? "Processing"
        setAgentStatus("working")
        // Narration before the FIRST tool is the turn's intro (frozen once).
        if (!toolsStartedRef.current) {
          toolsStartedRef.current = true
          if (streamingContentRef.current) {
            setIntroContent(streamingContentRef.current)
          }
        }
        // Clear the LIVE stream buffer at every tool boundary so the in-flight
        // view doesn't show between-tool narration ("let me do the next thing")
        // stacked above the progress list. This only affects the live display;
        // the persisted result bubble uses the authoritative server content on
        // completion (see resultContent), so nothing real is lost here.
        streamingContentRef.current = null
        setStreamingContent(null)
        updateActions((prev) => {
          const last = prev[prev.length - 1]
          // Collapse consecutive identical tool calls (e.g. creating many
          // contacts in a row) into a single item, and keep it the active/
          // shimmering one — otherwise the same label stacks up N times and the
          // shimmer ends up on a stale entry (IMP-366 follow-up).
          if (last?.label === label) {
            return prev.map((a, i) =>
              i === prev.length - 1
                ? { ...a, status: "active" as const }
                : a.status === "active"
                  ? { ...a, status: "completed" as const }
                  : a,
            )
          }
          return [
            ...prev.map((a) =>
              a.status === "active"
                ? { ...a, status: "completed" as const }
                : a,
            ),
            { label, status: "active" as const },
          ]
        })
      } else if (event.status === "tool_result") {
        updateActions((prev) =>
          prev.map((a) =>
            a.status === "active" ? { ...a, status: "completed" as const } : a,
          ),
        )
      }
    },
    [updateActions],
  )

  const onError = useCallback((message: string) => {
    setStreamingContent(null)
    streamingContentRef.current = null
    setIntroContent(null)
    toolsStartedRef.current = false
    setIsWaiting(false)
    setAgentActions([])
    agentActionsRef.current = []
    setAgentStatus("thinking")
    setMessages((prev) => [
      ...prev,
      {
        uid: `error-${Date.now()}`,
        role: "assistant" as const,
        content: `Error: ${message}`,
        model_id: null,
        input_tokens: null,
        output_tokens: null,
        created_at: new Date().toISOString(),
      },
    ])
  }, [])

  const onTitleUpdate = useCallback((title: string) => {
    setChatTitle(title)
  }, [])

  useChatChannel(chat.uid, {
    onConnected,
    onChunk,
    onMessageComplete,
    onError,
    onTitleUpdate,
    onStatus,
  })

  function handleAction(message: string) {
    handleSubmit(message)
  }

  function handleSubmit(message: string, files?: File[]) {
    const optimisticMessage: ChatMessage = {
      uid: `temp-${Date.now()}`,
      role: "user",
      content: message,
      model_id: null,
      input_tokens: null,
      output_tokens: null,
      created_at: new Date().toISOString(),
      attachments: files?.map((f) => ({
        filename: f.name,
        content_type: f.type,
        byte_size: f.size,
        url: "",
      })),
    }
    setMessages((prev) => [...prev, optimisticMessage])
    setIsWaiting(true)
    setAgentStatus("thinking")
    setAgentActions([])
    agentActionsRef.current = []
    // Start a fresh turn: clear the previous turn's intro/results stream latches.
    setStreamingContent(null)
    streamingContentRef.current = null
    setIntroContent(null)
    toolsStartedRef.current = false

    if (files && files.length > 0) {
      const formData = new FormData()
      formData.append("message", message)
      files.forEach((file) => formData.append("files[]", file))

      void fetch(messagesConversationPath(chat.uid), {
        method: "POST",
        headers: { "X-CSRF-Token": getCsrfToken() },
        body: formData,
      })
    } else {
      void fetch(messagesConversationPath(chat.uid), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        body: JSON.stringify({ message }),
      })
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={chatTitle ?? "Conversation"} />

      <div className="flex h-[calc(100dvh-5rem)] flex-col overflow-hidden">
        <MessageList
          messages={messages}
          introContent={introContent}
          streamingContent={streamingContent}
          isWaiting={isWaiting}
          agentStatus={agentStatus}
          agentActions={agentActions}
          onAction={handleAction}
        />

        <div className="mx-auto w-full max-w-2xl pb-4">
          <ChatInput
            onSubmit={handleSubmit}
            disabled={isWaiting || streamingContent !== null}
            placeholder="Reply..."
          />
          <p className="text-muted-foreground mt-2 text-center text-xs">
            AI can make mistakes. Please double check responses.
          </p>
        </div>
      </div>
    </AppLayout>
  )
}
